/**
 * Shared payment formatting utilities.
 * Extracted from 9+ files that had identical copies of these functions.
 */

import { getMemberDisplayName } from './name';
import type { DuesAssessmentWithUser } from '../types/payments';

/**
 * Display name for the payer on a dues assessment.
 * Prefers the linked User; falls back to FamilyMember; finally renders a placeholder.
 * Use this anywhere an assessment row or modal labels the person who owes the dues.
 */
export function getAssessmentPayerName(
  assessment: Pick<DuesAssessmentWithUser, 'user' | 'familyMember'>,
): string {
  if (assessment.user) {
    return `${assessment.user.firstName} ${assessment.user.lastName}`.trim() || 'Unknown member';
  }
  if (assessment.familyMember) {
    return getMemberDisplayName(assessment.familyMember);
  }
  return 'Unknown member';
}

/** Format a number as USD currency (e.g., $1,234.56). Always uses absolute value. */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Math.abs(amount));
}

/**
 * Coerce a decimal that may arrive as either a Drizzle decimal string
 * ("49.99"), a JS number (49.99), or null/undefined into a finite JS
 * number suitable for arithmetic and `formatCurrency`. Returns 0 for
 * null/undefined/malformed input.
 *
 * Use this whenever a value comes from a SellableItem or DonationCampaign
 * decimal field — the backend Drizzle layer serializes decimals as
 * strings, so direct arithmetic (`item.price * qty`) coerces invisibly
 * but comparisons (`item.memberPrice < item.price`) silently fall
 * back to lexicographic string compare ("100" < "25" → true).
 */
export function parsePrice(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Format a date string for display in payment lists (e.g., "Jan 15, 2026"). */
export function formatPaymentDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Map a payment status to Tailwind color classes for badges. */
export function getPaymentStatusColor(status: string): string {
  switch (status) {
    case 'completed':
    case 'paid':
      return 'text-green-600 bg-green-100 border-green-200';
    case 'pending':
      return 'text-text-tertiary bg-surface-1 border-border';
    case 'failed':
      return 'text-red-600 bg-red-100 border-red-200';
    case 'refunded':
    case 'partially_refunded':
      return 'text-orange-600 bg-orange-100 border-orange-200';
    default:
      return 'text-gray-600 bg-gray-100 border-gray-200';
  }
}

/** Map a payment type to Tailwind color classes. */
export function getPaymentTypeColor(type: string): string {
  switch (type) {
    case 'dues':
      return 'text-blue-600 bg-blue-100';
    case 'event':
      return 'text-purple-600 bg-purple-100';
    case 'donation':
      return 'text-green-600 bg-green-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

/** Map a dues status to Tailwind badge classes. */
export function getDuesStatusBadge(status: string): string {
  switch (status) {
    case 'paid':
      return 'text-green-600 bg-green-100';
    case 'pending':
      return 'text-yellow-600 bg-yellow-100';
    case 'partial':
      return 'text-blue-600 bg-blue-100';
    case 'overdue':
      return 'text-red-600 bg-red-100';
    case 'waived':
      return 'text-gray-600 bg-gray-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

/** Format a date string with time (e.g., "Jan 15, 2026, 3:45 PM"). */
export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Format a date string as a long date (e.g., "Wednesday, January 15, 2026"). */
export function formatLongDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Format a date string as a long date with time and timezone. */
export function formatLongDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export interface BalanceStatus {
  status: 'overdue' | 'credit' | 'current';
  label: string;
  color: string;
}

/** Derive a balance status object from outstanding/credit amounts. */
export function getBalanceStatus(outstanding: number, credits: number): BalanceStatus {
  if (outstanding > 0) {
    return { status: 'overdue', label: 'Amount Due', color: 'text-red-600 bg-red-100' };
  } else if (credits > 0) {
    return { status: 'credit', label: 'Credit Balance', color: 'text-green-600 bg-green-100' };
  }
  return { status: 'current', label: 'Current', color: 'text-green-600 bg-green-100' };
}

/** Display a human-readable payment method label with optional card details. */
export function getPaymentMethodLabel(
  method: string,
  cardLast4?: string,
  cardBrand?: string,
): string {
  switch (method) {
    case 'card':
      return cardBrand && cardLast4
        ? `${cardBrand.toUpperCase()} ending in ${cardLast4}`
        : 'Credit Card';
    case 'paypal':
      return 'PayPal';
    case 'bank_transfer':
      return 'Bank Transfer';
    case 'apple_pay':
      return 'Apple Pay';
    case 'google_pay':
      return 'Google Pay';
    default:
      return method;
  }
}
